/**
 * pipelines.ts — data layer for MULTIPLE PIPELINES (migration 0074).
 *
 * A `pipelines` row owns a set of pipeline_stages (and, transitively, the deals
 * and stage-gate rules scoped to it). Today the deals module ('crm_deals') has a
 * single default 'Sales Pipeline'; this layer is the seam that lets a workspace
 * add more without touching the board's shape.
 *
 * RLS (mirrors stage_gate_rules / automations): read → crm.read, write →
 * crm.manage, super_admin via '*'. workspace_id MUST be sent on insert (the RLS
 * WITH CHECK enforces it). Soft delete only — there is no hard delete by design.
 *
 * Conventions match the other src/lib modules (automations.ts / stageGates.ts):
 * fail-soft reads (a query error logs + returns []/null rather than throwing into
 * the UI), React-Query hooks for the shared cache, and a single shared query key.
 *
 * SCOPE: deals only. Leads are NOT pipeline-scoped (migration 0074 left them
 * untouched), so nothing here is called for crm_leads.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';
import { getCurrentWorkspaceId } from './apiClient';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface Pipeline {
  id: string;
  workspace_id: string;
  module_key: string;
  name: string;
  key: string;
  sort_order: number;
  is_default: boolean;
  is_active: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

export interface NewPipelineInput {
  name: string;
  /** Stable slug; derived from the name when omitted. */
  key?: string;
}

export type PipelinePatch = Partial<Pick<Pipeline, 'name' | 'sort_order' | 'is_active' | 'is_default'>>;

/** The module every deal pipeline belongs to. */
export const DEAL_MODULE_KEY = 'crm_deals';

// ─── Pure helpers ─────────────────────────────────────────────────────────────

/** Slugify a pipeline name into a stable key (a-z0-9_). */
export function slugifyPipeline(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'pipeline';
}

/** The pipeline new boards should select by default (flagged default, else first). */
export function defaultPipeline(pipelines: Pipeline[]): Pipeline | undefined {
  return pipelines.find(p => p.is_default) ?? pipelines[0];
}

export interface DeletePipelineCheck { ok: boolean; reason?: string; }

/** Whether a pipeline may be soft-deleted: never the default, never the last one. */
export function canDeletePipeline(pipeline: Pipeline, pipelines: Pipeline[]): DeletePipelineCheck {
  const live = pipelines.filter(p => !p.is_deleted);
  if (live.length <= 1) return { ok: false, reason: 'You can’t delete the last pipeline.' };
  if (pipeline.is_default) return { ok: false, reason: 'You can’t delete the default pipeline. Make another pipeline the default first.' };
  return { ok: true };
}

// ─── Data access ──────────────────────────────────────────────────────────────

/**
 * List a module's pipelines, non-deleted, ordered by sort_order then created_at.
 * Active-only by default (what the board offers); pass includeInactive for the
 * settings manager, which shows + toggles paused pipelines.
 */
export async function listPipelines(
  moduleKey: string = DEAL_MODULE_KEY,
  opts: { includeInactive?: boolean } = {},
): Promise<Pipeline[]> {
  let q = supabase
    .from('pipelines')
    .select('*')
    .eq('module_key', moduleKey)
    .eq('is_deleted', false)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (!opts.includeInactive) q = q.eq('is_active', true);

  const { data, error } = await q;
  if (error) {
    console.warn('[pipelines] listPipelines failed:', error.message);
    return [];
  }
  return (data ?? []) as Pipeline[];
}

/**
 * The id of a module's default pipeline (falls back to the first non-deleted one
 * if no row is flagged default). Used to resolve deal stages when no specific
 * pipeline is selected. Returns null only when the module has no pipelines yet.
 */
export async function getDefaultPipelineId(moduleKey: string = DEAL_MODULE_KEY): Promise<string | null> {
  const pipelines = await listPipelines(moduleKey);
  return defaultPipeline(pipelines)?.id ?? null;
}

/**
 * Create a pipeline. workspace_id MUST be sent (RLS WITH CHECK). New pipelines
 * are appended (sort_order = max + 1), active, and never default (the existing
 * default keeps the one-default-per-workspace guard satisfied).
 */
export async function createPipeline(
  input: NewPipelineInput,
  existing: Pipeline[] = [],
  moduleKey: string = DEAL_MODULE_KEY,
): Promise<Pipeline | null> {
  // Fail-soft like every other write in this file: warn and return null rather than throw.
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) {
    console.warn('[pipelines] createPipeline skipped — no workspace resolved yet');
    return null;
  }
  const sort_order = existing.length ? Math.max(...existing.map(p => p.sort_order)) + 1 : 0;
  const name = (input.name ?? '').trim() || 'New pipeline';
  const key = (input.key ?? slugifyPipeline(name));
  const { data, error } = await supabase
    .from('pipelines')
    .insert({
      workspace_id: workspaceId,
      module_key: moduleKey,
      name,
      key,
      sort_order,
      is_default: false,
      is_active: true,
    })
    .select('*')
    .single();
  if (error) {
    console.warn('[pipelines] createPipeline failed:', error.message);
    return null;
  }
  return data as Pipeline;
}

/** Patch a pipeline — rename, reorder (sort_order), or toggle is_active. */
export async function updatePipeline(id: string, patch: PipelinePatch): Promise<Pipeline | null> {
  const { data, error } = await supabase
    .from('pipelines')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select('*')
    .single();
  if (error) {
    console.warn('[pipelines] updatePipeline failed:', error.message);
    return null;
  }
  return data as Pipeline;
}

/**
 * Make exactly one pipeline the default for a module. Clears the flag on the
 * others FIRST (the partial unique index uq_pipelines_one_default rejects two
 * defaults), then sets it on the target. A default pipeline is also forced
 * active — the board always needs a selectable default.
 */
export async function setDefaultPipeline(id: string, moduleKey: string = DEAL_MODULE_KEY): Promise<boolean> {
  const { error: clearErr } = await supabase
    .from('pipelines')
    .update({ is_default: false, updated_at: new Date().toISOString() })
    .eq('module_key', moduleKey)
    .eq('is_deleted', false)
    .neq('id', id);
  if (clearErr) {
    console.warn('[pipelines] setDefaultPipeline clear failed:', clearErr.message);
    return false;
  }
  const { error } = await supabase
    .from('pipelines')
    .update({ is_default: true, is_active: true, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) {
    console.warn('[pipelines] setDefaultPipeline set failed:', error.message);
    return false;
  }
  return true;
}

/** Soft-delete a pipeline (is_deleted=true). Callers guard via canDeletePipeline. */
export async function softDeletePipeline(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('pipelines')
    .update({ is_deleted: true, is_default: false, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) {
    console.warn('[pipelines] softDeletePipeline failed:', error.message);
    return false;
  }
  return true;
}

/** Persist a new pipeline order — writes sort_order = index for each id. */
export async function reorderPipelines(orderedIds: string[]): Promise<boolean> {
  const results = await Promise.all(
    orderedIds.map((id, i) =>
      supabase.from('pipelines').update({ sort_order: i, updated_at: new Date().toISOString() }).eq('id', id)
        .then(({ error }) => !error),
    ),
  );
  return results.every(Boolean);
}

// ─── React-Query hook ───────────────────────────────────────────────────────

/** Shared query key so the board, switcher and settings manager read one cache. */
export const pipelinesQueryKey = (moduleKey: string, includeInactive = false) =>
  ['pipelines', moduleKey, includeInactive] as const;

/**
 * Read a module's pipelines (active-only by default). Multiple callers across the
 * board switcher + settings dedupe to one fetch via React Query.
 */
const EMPTY_PIPELINES: Pipeline[] = [];

export function usePipelines(
  moduleKey: string = DEAL_MODULE_KEY,
  opts: { includeInactive?: boolean } = {},
) {
  const includeInactive = !!opts.includeInactive;
  const q = useQuery({
    queryKey: pipelinesQueryKey(moduleKey, includeInactive),
    queryFn: () => listPipelines(moduleKey, { includeInactive }),
    staleTime: 60_000,
  });
  return {
    // Stable identity while loading (see sections.ts — same effect-loop hazard).
    pipelines: q.data ?? EMPTY_PIPELINES,
    isLoading: q.isLoading,
    error: q.error as Error | null,
    refetch: q.refetch,
  };
}
